import { useEffect, useMemo, useRef, useState } from "react";

type UseObserverProps = {
  onIntersect: (entry: IntersectionObserverEntry) => void;
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  enabled?: boolean;
};

type UseMultiObserverProps = {
  elements: Array<Element | null>;
  onIntersect: (entries: IntersectionObserverEntry[]) => void;
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  enabled?: boolean;
};

/**
 * Observer hook
 * @param onIntersect Callback function when element is intersecting
 * @param root Root element for the observer
 * @param rootMargin Margin around the root element
 * @param threshold Threshold for the observer
 * @param enabled Whether the observer is enabled
 * @returns Ref to the observed element
 */
export const useObserver = ({
  onIntersect,
  root = null,
  rootMargin = "0px",
  threshold = 0,
  enabled = true,
}: UseObserverProps) => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!targetRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onIntersect(entry);
          }
        });
      },
      {
        root,
        rootMargin,
        threshold,
      },
    );

    observerRef.current.observe(targetRef.current);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [onIntersect, root, rootMargin, threshold, enabled]);

  return targetRef;
};

/**
 * Observe multiple existing DOM elements.
 * Useful for active-section tracking and similar multi-target cases.
 */
export const useMultiObserver = ({
  elements,
  onIntersect,
  root = null,
  rootMargin = "0px",
  threshold = 0,
  enabled = true,
}: UseMultiObserverProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const validElements = useMemo(
    () => elements.filter((element): element is Element => Boolean(element)),
    [elements],
  );

  useEffect(() => {
    if (!enabled || validElements.length === 0) return;

    observerRef.current = new IntersectionObserver(onIntersect, {
      root,
      rootMargin,
      threshold,
    });

    validElements.forEach((element) => observerRef.current?.observe(element));

    return () => {
      validElements.forEach((element) =>
        observerRef.current?.unobserve(element),
      );
      observerRef.current?.disconnect();
    };
  }, [enabled, onIntersect, root, rootMargin, threshold, validElements]);
};

/**
 * Hook to trigger loading when an element approaches the viewport.
 * Automatically disconnects once in view to prevent overhead.
 */
export const useInView = (rootMargin = "300px") => {
  const targetRef = useRef<HTMLDivElement | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = targetRef.current;
    if (!el || isInView) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [isInView, rootMargin]);

  return [targetRef, isInView] as const;
};
